import React, { useState, useEffect } from 'react';
import '../styles/Timekeeper.css';
import { Checkbox } from './FieldComponents';
import Modal from './Modal';
import { RefreshCcw, ChevronLeft, ChevronRight, Clock, Plus } from 'lucide-react';
import Alert from './Alert';
import { format } from 'date-fns';
import { DateTime } from 'luxon';

export const Timekeeper = ({ user_id }) => {
    const [pto, setPto] = useState([]);
    const [ptoTypes, setPtoTypes] = useState([]);
    const [ptoType, setPtoType] = useState(0);
    const [ptoStart, setPtoStart] = useState(new Date().toISOString().slice(0, 16));
    const [ptoEnd, setPtoEnd] = useState(new Date().toISOString().slice(0, 16));
    const [ptoNote, setPtoNote] = useState('');
    const [ptoStatus, setPtoStatus] = useState(0);
    const [ptoPrev, setPtoPrev] = useState(0);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [currentScreen, setCurrentScreen] = useState(0);
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [calendarLoading, setCalendarLoading] = useState(false);
    const [calendarError, setCalendarError] = useState(null);
    const [showEventForm, setShowEventForm] = useState(false);
    const [newEvent, setNewEvent] = useState({
        subject: '',
        start_date: new Date().toISOString().slice(0, 16),
        end_date: new Date().toISOString().slice(0, 16),
        notes: '',
        is_all_day: true
    });
    const [createEventStatus, setCreateEventStatus] = useState(null);
    const [userAccess, setUserAccess] = useState('user');
    const [ptoRequests, setPtoRequests] = useState([]);
    const [previousRequests, setPreviousRequests] = useState([]);
    const [newPtoRequest, setNewPtoRequest] = useState({
        start_date: new Date().toISOString().slice(0, 10),
        end_date: new Date().toISOString().slice(0, 10),
        start_time: new Date().toISOString().slice(11, 16),
        end_time: new Date().toISOString().slice(11, 16),
        request_type: 'pto',
        notes: '',
        is_all_day: true
    });
    const [submittingRequest, setSubmittingRequest] = useState(false);
    const [requestStatus, setRequestStatus] = useState(null);

    const [processingRequestId, setProcessingRequestId] = useState(null);
    const [approvalFeedback, setApprovalFeedback] = useState({ show: false, type: '', message: '' });
    const [requestsPage, setRequestsPage] = useState(1);
    const [requestsPerPage] = useState(10);
    const [filterStatus, setFilterStatus] = useState('all');
    const [newRequest, setNewRequest] = useState(null);
    const [timeEntries, setTimeEntries] = useState([]);
    const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStartDate(new Date()));
    const [loadingTimeEntries, setLoadingTimeEntries] = useState(false);
    const [clockInTime, setClockInTime] = useState('');
    const [showClockModal, setShowClockModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [modalType, setModalType] = useState('in');
    const [timeModalData, setTimeModalData] = useState({
        date: new Date(),
        time: new Date().toTimeString().slice(0, 5),
        notes: ''
    });

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    function getWeekStartDate(date) {
        const newDate = new Date(date);
        const day = newDate.getDay();
        newDate.setDate(newDate.getDate() - day);
        return new Date(newDate.setHours(0, 0, 0, 0));
    }

    const fetchPto = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/pto.php?user_id=${user_id}`);
            const data = await response.json();
            setPto(data.pto || []);
        } catch (error) {
            console.error("Error fetching PTO:", error);
        }
    };

    const fetchCalendarEvents = async () => {
        setCalendarLoading(true);
        setCalendarError(null);
        try {
            const response = await fetch(`https://api.casedb.co/get-calendar-events.php?user_id=${user_id}`);
            const data = await response.json();
            
            if (data.error) {
                setCalendarError(data.error);
                
                if (data.auth_required) {
                    setCalendarError("Microsoft account authentication required");
                }
            } else {
                setCalendarEvents(data.value || []);
            }
        } catch (error) {
            console.error("Error fetching calendar events:", error);
            setCalendarError("Failed to fetch calendar events");
        } finally {
            setCalendarLoading(false);
        }
    };

    const connectMicrosoftAccount = () => {
        window.location.href = "https://api.casedb.co/graph_login.php";
    };

    const fetchUserAccess = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/session.php?time=${new Date().getTime()}`, { method: 'GET', credentials: 'include' });
            const data = await response.json();
            
            if (data.access_level || data.timekeeper) {
                setUserAccess(data.timekeeper ?? data.access_level);
            }
        } catch (error) {
            console.error("Error fetching user access level:", error);
        }
    };

    const fetchPtoRequests = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/pto.php?user_id=${user_id}`);
            const data = await response.json();
            
            if (data.requests) {
                setPtoRequests(data.requests);
                setPreviousRequests(data.previous_requests || []);
            }
        } catch (error) {
            console.error("Error fetching PTO requests:", error);
        }
    };

    const fetchTimeEntries = async () => {
        setLoadingTimeEntries(true);
        try {
            const weekEnd = new Date(currentWeekStart);
            weekEnd.setDate(weekEnd.getDate() + 6);
            
            const startDateStr = currentWeekStart.toISOString().split('T')[0];
            const endDateStr = weekEnd.toISOString().split('T')[0];
            
            const response = await fetch(
                `https://api.casedb.co/timekeeper.php?user_id=${user_id}&start_date=${startDateStr}&end_date=${endDateStr}`
            );
            
            const data = await response.json();
            if (data.success && data.entries) {
                setTimeEntries(data.entries);
            } else {
                console.error("Error fetching time entries:", data.error || "Unknown error");
            }
        } catch (error) {
            console.error("Error fetching time entries:", error);
        } finally {
            setLoadingTimeEntries(false);
        }
    };

    useEffect(() => {
        fetchPto();
        fetchCalendarEvents();
        fetchUserAccess();
        fetchPtoRequests();
    }, []);

    useEffect(() => {
        if (currentScreen === 0) {
            fetchTimeEntries();
        }
    }, [currentScreen, currentWeekStart]);

    const formatDate = (dateString) => {
        const dt = DateTime.fromFormat(dateString, 'yyyy-MM-dd HH:mm:ss', { zone: 'local' });
        return dt.toFormat("EEE, MMM d, yyyy 'at' h:mm a");
    };

    const formatTime = (dateString) => {
        return new Date(dateString);
    };

    const formatCalendarDate = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatCalendarTime = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleAllDayToggle = () => {
        setNewEvent(prev => {
            if (!prev.is_all_day) {
                const start = new Date(prev.start_date || new Date());
                const startOfDay = new Date(start.setHours(0, 0, 0, 0));
                const endOfDay = new Date(startOfDay);
                endOfDay.setDate(endOfDay.getDate() + 1);
    
                return {
                    ...prev,
                    is_all_day: true,
                    start_date: format(startOfDay, "yyyy-MM-dd'T'HH:mm"),
                    end_date: format(endOfDay, "yyyy-MM-dd'T'HH:mm")
                };
            } else {
                return {
                    ...prev,
                    is_all_day: false
                };
            }
        });
    };

    const handleCreateEvent = async (e) => {
        e.preventDefault();
        setCreateEventStatus('loading');
        
        const formData = new FormData();
        formData.append('user_id', user_id);
        formData.append('subject', newEvent.subject);
        formData.append('start_date', newEvent.start_date);
        formData.append('end_date', newEvent.end_date);
        formData.append('notes', newEvent.notes);
        formData.append('is_all_day', newEvent.is_all_day ? '1' : '0');

        try {
            const response = await fetch('https://api.casedb.co/create-event.php', {
                method: 'POST',
                body: formData
            });
            
            const result = await response.json();
            
            if (result.success) {
                setCreateEventStatus('success');
                setShowEventForm(false);
                setNewEvent({
                    subject: '',
                    start_date: new Date().toISOString().slice(0, 16),
                    end_date: new Date().toISOString().slice(0, 16),
                    notes: '',
                    is_all_day: true
                });
                
                setTimeout(() => {
                    fetchCalendarEvents();
                    setCreateEventStatus(null);
                }, 1500);
            } else {
                setCreateEventStatus('error');
                console.error("Error creating event:", result.error);
                
                if (result.error?.includes('Authentication') || result.error?.includes('token')) {
                    setCreateEventStatus('auth_error');
                }
            }
        } catch (error) {
            setCreateEventStatus('error');
            console.error("Error creating event:", error);
        }
    };

    const handleEventFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewEvent(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const isAdmin = () => {
        return ['timekeeper', 'admin', 'global admin', 1].includes(userAccess);
    };

    const handlePtoRequestChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'is_all_day') {
            setNewPtoRequest(prev => ({
                ...prev,
                is_all_day: checked
            }));
            return;
        }
        
        setNewPtoRequest(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handlePtoRequestSubmit = async (e) => {
        e.preventDefault();
        setSubmittingRequest(true);
        setRequestStatus(null);
        setNewRequest(null);
        
        let startDate = newPtoRequest.start_date;
        let endDate = newPtoRequest.end_date;
        
        if (!newPtoRequest.is_all_day) {
            startDate = `${newPtoRequest.start_date}T${newPtoRequest.start_time}`;
            endDate = `${newPtoRequest.end_date}T${newPtoRequest.end_time}`;
        }
        
        const formData = new FormData();
        formData.append('user_id', user_id);
        formData.append('start_date', startDate);
        formData.append('end_date', endDate);
        formData.append('request_type', newPtoRequest.request_type);
        formData.append('notes', newPtoRequest.notes);
        formData.append('is_all_day', newPtoRequest.is_all_day ? '1' : '0');
        formData.append('action', 'create');
        
        try {
            const response = await fetch('https://api.casedb.co/pto.php', {
                method: 'POST',
                body: formData
            });
            
            const result = await response.json();
            
            if (result.success) {
                setRequestStatus('success');
                setNewPtoRequest({
                    start_date: new Date().toISOString().slice(0, 10),
                    end_date: new Date().toISOString().slice(0, 10),
                    request_type: 'pto',
                    notes: '',
                    is_all_day: true
                });
                
                fetchPtoRequests();
            } else {
                setRequestStatus('error');
                console.error("Error submitting request:", result.error);
            }
        } catch (error) {
            setRequestStatus('error');
            console.error("Error submitting request:", error);
        } finally {
            setSubmittingRequest(false);
        }
    };

    const changeEventDetails = async (event) => {
        const response = await fetch(`https://api.casedb.co/get_event.php?event_id=${event?.id ?? 'AAMkAGFlZTMzZGQzLWFiMWEtNDg1My05Mjk0LTM2MWEwMzc4ODU3NwBGAAAAAADxvKjr9QtiTJOrmSNTnfxtBwBn8OvsdmIkSr0G-sdQwc7zAAAAAAENAABn8OvsdmIkSr0G-sdQwc7zAADe-bnMAAA='}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                start: {
                    dateTime: "2025-07-02T10:00:00",
                    timeZone: "Central Standard Time"
                },
                end: {
                    dateTime: "2025-07-02T11:00:00",
                    timeZone: "Central Standard Time"
                },
                subject: "Updated via frontend"
            })
        });
        const data = await response.json();
        console.log("Event updated:", data);
    }

    useEffect(() => {
        changeEventDetails();
    }, []);

    const handlePtoApproval = async (requestId, approve) => {
        setProcessingRequestId(requestId);
        
        const formData = new FormData();
        formData.append('request_id', requestId);
        formData.append('action', approve ? 'approve' : 'reject');
        formData.append('user_id', user_id);
        
        try {
            const response = await fetch('https://api.casedb.co/pto.php', {
                method: 'POST',
                body: formData
            });
            
            const result = await response.json();
            
            if (result.success) {
                setApprovalFeedback({
                    show: true,
                    type: approve ? 'success' : 'error',
                    message: `Request ${approve ? 'approved' : 'rejected'} successfully!`
                });
                
                setTimeout(() => {
                    setApprovalFeedback({ show: false, type: '', message: '' });
                }, 3000);
                
                fetchPtoRequests();
            } else {
                console.error(`Failed to ${approve ? 'approve' : 'reject'} request:`, result.error);
                alert(`Failed to ${approve ? 'approve' : 'reject'} the request: ${result.error}`);
            }
        } catch (error) {
            console.error(`Error ${approve ? 'approving' : 'rejecting'} request:`, error);
            alert(`Error ${approve ? 'approving' : 'rejecting'} request`);
        } finally {
            setProcessingRequestId(null);
        }
    };

    const renderEventForm = () => {
        return (
            <div className="event-form-overlay">
                <div className="event-form-container">
                    <button 
                        className="close-form-button" 
                        onClick={() => {
                            setShowEventForm(false);
                            setCreateEventStatus(null);
                        }}
                    >
                        &times;
                    </button>
                    
                    <h2>Create New Calendar Event</h2>
                    
                    {createEventStatus === 'success' && (
                        <div className="status-message success">
                            Event created successfully!
                        </div>
                    )}
                    
                    {createEventStatus === 'error' && (
                        <div className="status-message error">
                            Failed to create event. Please try again.
                        </div>
                    )}
                    
                    {createEventStatus === 'auth_error' && (
                        <div className="status-message error">
                            Authentication error. Please reconnect your Microsoft account.
                            <button 
                                className="reconnect-button"
                                onClick={connectMicrosoftAccount}
                            >
                                Reconnect
                            </button>
                        </div>
                    )}
                    
                    <form onSubmit={handleCreateEvent}>
                        <div className="form-group">
                            <label htmlFor="subject">Event Title</label>
                            <input 
                                type="text" 
                                id="subject" 
                                name="subject"
                                value={newEvent.subject}
                                onChange={handleEventFormChange}
                                placeholder="Meeting, Appointment, etc."
                                required
                            />
                        </div>
                        
                        <div className='form-group'>
                            <Checkbox
                                value={newEvent.is_all_day}
                                onChange={handleAllDayToggle}
                                label="All day event?"
                                hint="If this event lasts the entire day, check this box."
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="start_date">Start Date/Time</label>
                            <input 
                                type={newEvent.is_all_day ? "date" : "datetime-local"} 
                                id="start_date" 
                                name="start_date"
                                value={newEvent.start_date}
                                onChange={handleEventFormChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="end_date">End Date/Time</label>
                            <input 
                                type={newEvent.is_all_day ? "date" : "datetime-local"} 
                                id="end_date" 
                                name="end_date"
                                value={newEvent.end_date}
                                onChange={handleEventFormChange}
                                required
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="notes">Notes</label>
                            <textarea 
                                id="notes" 
                                name="notes"
                                value={newEvent.notes}
                                onChange={handleEventFormChange}
                                placeholder="Add any notes or details about this event"
                                rows="4"
                            ></textarea>
                        </div>
                        
                        <button 
                            type="submit" 
                            className="submit-event-button"
                            disabled={createEventStatus === 'loading'}
                        >
                            {createEventStatus === 'loading' ? 'Creating...' : 'Create Event'}
                        </button>
                    </form>
                </div>
            </div>
        );
    };

    const renderRequestForm = () => {
        return (
            <Modal
                onClose={() => setNewRequest(null)}
                title='Submit Time-Off Request'
                single
                footer={(
                    <div className='modal-footer-actions'>
                        <button className='action alt' onClick={() => setNewRequest(null)}>Cancel</button>
                        <button className='action' onClick={handlePtoRequestSubmit} disabled={submittingRequest}>
                            {submittingRequest ? 'Submitting...' : 'Submit Request'}
                        </button>
                    </div>
                )}
            >
                <div className='modal-content-wrapper'>
                    <div className="form-group upper">
                        <label htmlFor="request_type" className='subtext'>Request Type</label>
                        <select
                            id="request_type"
                            name="request_type"
                            className='default-select full'
                            value={newPtoRequest.request_type}
                            onChange={handlePtoRequestChange}
                            required
                        >
                            <option value="pto">Paid Time Off</option>
                            <option value="ooo">Out of Office</option>
                        </select>
                    </div>
                    <>
                        {newPtoRequest.is_all_day ? (
                            <>
                                <div className="form-group upper">
                                    <label htmlFor="start_date" className='subtext'>Start Date</label>
                                    <input
                                        type="date"
                                        id="start_date"
                                        name="start_date"
                                        value={newPtoRequest.start_date}
                                        onChange={handlePtoRequestChange}
                                        required
                                    />
                                </div>
                                <div className='form-group contained-upper'>
                                    <label htmlFor="end_date" className='subtext'>End Date</label>
                                    <input
                                        type="date"
                                        id="end_date"
                                        name="end_date"
                                        value={newPtoRequest.end_date}
                                        onChange={handlePtoRequestChange}
                                        required
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className='half-input-container'>
                                    <div className='form-group half-input upper'>
                                        <label htmlFor="start_date" className='subtext'>Start Date</label>
                                        <input
                                            type="date"
                                            id="start_date"
                                            name="start_date"
                                            value={newPtoRequest.start_date}
                                            onChange={handlePtoRequestChange}
                                            required
                                        />
                                    </div>
                                    <div className='form-group half-input upper'>
                                        <label htmlFor="start_time" className='subtext'>Start Time</label>
                                        <input 
                                            type='time' 
                                            id='start_time'
                                            name='start_time'
                                            value={newPtoRequest.start_time}
                                            onChange={handlePtoRequestChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className='half-input-container'>
                                    <div className='form-group half-input contained-upper'>
                                        <label htmlFor="end_date" className='subtext'>End Date</label>
                                        <input
                                            type="date"
                                            id="end_date"
                                            name="end_date"
                                            value={newPtoRequest.end_date}
                                            onChange={handlePtoRequestChange}
                                            required
                                        />
                                    </div>
                                    <div className='form-group half-input contained-upper'>
                                        <label htmlFor="end_time" className='subtext'>End Time</label>
                                        <input 
                                            type='time' 
                                            id='end_time'
                                            name='end_time'
                                            value={newPtoRequest.end_time}
                                            onChange={handlePtoRequestChange}
                                            required
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                    <Checkbox 
                        value={newPtoRequest.is_all_day}
                        onChange={() => setNewPtoRequest(prev => ({ ...prev, is_all_day: !prev.is_all_day }))}
                        label='All day?'
                        hint='If the request is for a full day, check this box.'
                    />
                    <div className="form-group upper">
                        <label htmlFor="notes" className='subtext'>Notes</label>
                        <textarea
                            id="notes"
                            name="notes"
                            value={newPtoRequest.notes}
                            onChange={handlePtoRequestChange}
                            placeholder="Add any additional information"
                            rows="3"
                        ></textarea>
                    </div>
                </div>
            </Modal>
        );
    };

    const renderUserRequests = () => {
        if (!ptoRequests || ptoRequests.length === 0) {
            return <p>No requests found.</p>;
        }
        
        return (
            <div className="user-requests">
                <h2>Your Requests</h2>
                <div className="requests-list">
                    {ptoRequests.map(request => (
                        <div key={request.id} className={`request-item status-${request.status}`}>
                            <div className="request-type">
                                {request.request_type === 'pto' ? 'Paid Time Off' : 'Out of Office'}
                            </div>
                            <div className="request-dates">
                                {formatDate(request.start_date)} - {formatDate(request.end_date)}
                            </div>
                            <div className="request-status">
                                {request.status === 'pending' ? 'Pending Approval' : 
                                 request.status === 'approved' ? 'Approved' : 'Rejected'}
                            </div>
                            {request.notes && <div className="request-notes">{request.notes}</div>}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderAdminApproval = () => {
        const pendingRequests = ptoRequests.filter(req => req.status === 'pending');
        
        if (pendingRequests.length === 0) {
            return (
                <div className="admin-approval">
                    <h2>Time-Off Request Approval</h2>
                    <p>No pending requests to approve.</p>
                    
                    {renderRequestHistory()}
                </div>
            );
        }
        
        return (
            <div className="admin-approval">
                <h2>Time-Off Request Approval</h2>
                <div className="pending-requests">
                    {pendingRequests.map(request => (
                        <div key={request.id} className="pending-request-item">
                            <div className="request-header">
                                <span className="employee-name">{request.employee_name}</span>
                                <span className="request-type">
                                    {request.request_type === 'pto' ? 'PTO' : 'OOO'}
                                </span>
                            </div>
                            <div className="request-dates">
                                {formatDate(request.start_date)} - {formatDate(request.end_date)}
                            </div>
                            {request.notes && <div className="request-notes">{request.notes}</div>}
                            <div className="approval-actions">
                                <button 
                                    className="approve-button"
                                    onClick={() => handlePtoApproval(request.id, true)}
                                    disabled={processingRequestId === request.id}
                                >
                                    {processingRequestId === request.id ? 'Processing...' : 'Approve'}
                                </button>
                                <button 
                                    className="reject-button"
                                    onClick={() => handlePtoApproval(request.id, false)}
                                    disabled={processingRequestId === request.id}
                                >
                                    {processingRequestId === request.id ? 'Processing...' : 'Reject'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                {renderRequestHistory()}
            </div>
        );
    };

    const getPtoForDay = (date) => {
        const dateStr = date.toISOString().split('T')[0];
        return ptoRequests.find(req => {
            const start = new Date(req.start_date).toISOString().split('T')[0];
            const end = new Date(req.end_date).toISOString().split('T')[0];
            return dateStr >= start && dateStr <= end;
        });
    };

    const renderTimekeeper = () => {
        const weekDays = Array.from({ length: 7 }, (_, i) => {
            const day = new Date(currentWeekStart);
            day.setDate(day.getDate() + i);
            return day;
        });
        
        return (
            <div className="timekeeper-container">
                <div className="week-navigation">
                    <button className="nav-button" onClick={goToPreviousWeek}>
                        <ChevronLeft size={20} />
                    </button>
                    <div className="week-info">
                        {screenWidth >= 768 ? (
                            <span>
                                {currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} 
                                {' - '} 
                                {new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000)
                                    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                        ) : (
                            <span>
                                {currentWeekStart.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}
                                {' - '}
                                {new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000)
                                    .toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}
                            </span>
                        )}
                        <button className="current-week-button" onClick={goToCurrentWeek}>
                            Current Week
                        </button>
                    </div>
                    <button className="nav-button" onClick={goToNextWeek}>
                        <ChevronRight size={20} />
                    </button>
                </div>
                
                <div className="timekeeper-grid">
                    {weekDays.map((day, index) => {
                        const dayEntries = getDayTimeEntries(day);
                        const clockAction = getClockActionForDay(day);
                        const isCurrentDay = isToday(day);
                        
                        return (
                            <div 
                                key={index} 
                                className={`day-cell ${isCurrentDay ? 'current-day' : ''}`}
                            >
                                <div className="day-header">
                                    <div className="day-name">{formatDayName(day)}</div>
                                    <div className="day-number">{formatDayNumber(day)}</div>
                                </div>
                                
                                {dayEntries.length > 0 && <div className="time-entries-container">
                                    {dayEntries.map((entry, entryIndex) => (
                                        <div 
                                            key={entryIndex} 
                                            className={`time-entry ${entry.type}`}
                                            title={entry.notes || ''}
                                        >
                                            <Clock size={14} />
                                            <span className="entry-time">
                                                {formatTimeEntry(entry.timestamp)}
                                            </span>
                                            <span className="entry-type">
                                                {entry.type === 'in' ? 'In' : 'Out'}
                                            </span>
                                        </div>
                                    ))}
                                </div>}
                                
                                {getPtoForDay(day) && <span className="time-entry time-off" title="PTO scheduled">{String(getPtoForDay(day).request_type).toLocaleUpperCase()}</span>}
                                
                                <button 
                                    className={`clock-action-button ${clockAction}`}
                                    onClick={() => openClockModal(day, clockAction)}
                                >
                                    <Plus size={16} />
                                    Clock {clockAction === 'in' ? 'In' : 'Out'}
                                </button>
                            </div>
                        );
                    })}
                </div>
                
                {loadingTimeEntries && (
                    <div className="loading-overlay">
                        <div className="loading-spinner"></div>
                        <p>Loading time entries...</p>
                    </div>
                )}
                
                {renderClockModal()}
            </div>
        );
    };

    const renderCalendar = () => {
        if (calendarLoading) return <div className="calendar-loading">Loading calendar events...</div>;
        
        if (calendarError) {
            if (calendarError.includes("authentication required") || calendarError.includes("token")) {
                return (
                    <div className="calendar-auth-required">
                        <h3>Microsoft Calendar Access Required</h3>
                        <p>To view your calendar events, you need to connect your Microsoft account.</p>
                        <button className="connect-ms-button" onClick={connectMicrosoftAccount}>
                            Connect Microsoft Account
                        </button>
                    </div>
                );
            }
            return <div className="calendar-error">Error: {calendarError}</div>;
        }
        
        return (
            <div className="calendar-container">
                <div className="calendar-header">
                    <h2>Your Calendar</h2>
                    <div className='calendar-actions'>
                        <button className="refresh-calendar" onClick={fetchCalendarEvents}>
                            <RefreshCcw size={18} />
                        </button>
                        <button 
                            className="add-event-button"
                            onClick={() => setShowEventForm(true)}
                        >
                            {screenWidth >= 768 ? <><Plus size={16} />New event</> : <Plus size={16} />}
                        </button>
                    </div>
                </div>
                
                {calendarEvents.length === 0 ? (
                    <p>No upcoming events found</p>
                ) : (
                    <ul className="calendar-events">
                        {calendarEvents.map((event, index) => (
                            <li key={index} className="calendar-event">
                                <div className="event-subject">{event.subject}</div>
                                {event.isAllDay ? (
                                    <div className="event-time">All day - {formatCalendarDate(event.start.dateTime)}</div>
                                ) : (
                                    <div className="event-time">
                                        {formatCalendarDate(event.start.dateTime)} {formatCalendarTime(event.start.dateTime)} - {formatCalendarTime(event.end.dateTime)}
                                    </div>
                                )}
                                {event.bodyPreview && <div className="event-preview">{event.bodyPreview}</div>}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    };

    const convertToCSTString = (utcDateString) => {
        const date = new Date(utcDateString);
        const options = { 
            timeZone: 'America/Chicago',
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
        };
        return date.toLocaleString('en-US', options);
    };
    
    const formatTimeEntry = (timestamp) => {
        return convertToCSTString(timestamp);
    };

    const handleClockSubmit = async () => {
        try {
            const formData = new FormData();
            formData.append('user_id', user_id);
            formData.append('action', modalType === 'in' ? 'clock_in' : 'clock_out');
            
            const localDate = new Date(timeModalData.date);
            const [hours, minutes] = timeModalData.time.split(':');
            
            localDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
            
            const localISOString = localDate.toISOString();
            
            formData.append('timestamp', localISOString);
            formData.append('timezone', 'local');
            formData.append('actual_hours', hours);
            formData.append('actual_minutes', minutes);
            formData.append('notes', timeModalData.notes);
            
            const response = await fetch('https://api.casedb.co/timekeeper.php', {
                method: 'POST',
                body: formData
            });
            
            const data = await response.json();
            if (data.success) {
                fetchTimeEntries();
                setShowClockModal(false);
                setTimeModalData({
                    date: new Date(),
                    time: new Date().toTimeString().slice(0, 5),
                    notes: ''
                });
            } else {
                console.error("Error submitting time entry:", data.error || "Unknown error");
                alert("Failed to record time: " + (data.error || "Unknown error"));
            }
        } catch (error) {
            console.error("Error submitting time entry:", error);
            alert("Failed to record time. Please try again.");
        }
    };

    const goToPreviousWeek = () => {
        const prevWeek = new Date(currentWeekStart);
        prevWeek.setDate(prevWeek.getDate() - 7);
        setCurrentWeekStart(prevWeek);
    };

    const goToNextWeek = () => {
        const nextWeek = new Date(currentWeekStart);
        nextWeek.setDate(nextWeek.getDate() + 7);
        setCurrentWeekStart(nextWeek);
    };

    const goToCurrentWeek = () => {
        setCurrentWeekStart(getWeekStartDate(new Date()));
    };

    const openClockModal = (date, type) => {
        const selectedDate = new Date(date);
        setSelectedDate(selectedDate);
        setModalType(type);
        setTimeModalData({
            date: selectedDate,
            time: new Date().toTimeString().slice(0, 5),
            notes: ''
        });
        setShowClockModal(true);
    };

    const handleTimeModalChange = (e) => {
        const { name, value } = e.target;
        setTimeModalData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const getDayTimeEntries = (date) => {
        const dateStr = date.toISOString().split('T')[0];
        return timeEntries.filter(entry => 
            new Date(entry.timestamp).toISOString().split('T')[0] === dateStr
        ).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    };

    const getClockActionForDay = (date) => {
        const dayEntries = getDayTimeEntries(date);
        if (dayEntries.length === 0) return 'in';
        return dayEntries.length % 2 === 0 ? 'in' : 'out';
    };

    const formatDayName = (date) => {
        return new Date(date).toLocaleDateString('en-US', { weekday: 'short' });
    };

    const formatDayNumber = (date) => {
        return new Date(date).getDate();
    };

    const renderClockModal = () => {
        if (!showClockModal) return null;
        
        const actionText = modalType === 'in' ? 'Clock In' : 'Clock Out';
        
        return (
            <Modal
                onClose={() => setShowClockModal(false)}
                title={actionText}
                single
                footer={(
                    <div className='modal-footer-actions'>
                        <button className='action alt' onClick={() => setShowClockModal(false)}>Cancel</button>
                        <button className='action' onClick={handleClockSubmit}>
                            {actionText}
                        </button>
                    </div>
                )}
            >
                <div className='modal-content-wrapper'>
                    <div className="form-group upper">
                        <label htmlFor="date" className='subtext'>Date</label>
                        <input
                            type="date"
                            id="date"
                            name="date"
                            value={timeModalData.date.toISOString().split('T')[0]}
                            onChange={handleTimeModalChange}
                            required
                        />
                    </div>
                    <div className="form-group upper">
                        <label htmlFor="time" className='subtext'>Time</label>
                        <input
                            type="time"
                            id="time"
                            name="time"
                            value={timeModalData.time}
                            onChange={handleTimeModalChange}
                            required
                        />
                    </div>
                    <div className="form-group upper">
                        <label htmlFor="notes" className='subtext'>Notes (optional)</label>
                        <textarea
                            id="notes"
                            name="notes"
                            value={timeModalData.notes}
                            onChange={handleTimeModalChange}
                            placeholder="Add any notes about this time entry"
                            rows="3"
                        ></textarea>
                    </div>
                </div>
            </Modal>
        );
    };

    const renderRequestHistory = () => {
        const filteredRequests = filterStatus === 'all'
            ? ptoRequests.filter(req => req.status !== 'pending')
            : ptoRequests.filter(req => req.status === filterStatus);

        const indexOfLastRequest = requestsPage * requestsPerPage;
        const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
        const currentRequests = filteredRequests.slice(indexOfFirstRequest, indexOfLastRequest);
        const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);

        const paginate = (pageNumber) => setRequestsPage(pageNumber);

        return (
            <div className="requests-history">
                <h3>Request History</h3>
                <div className="filter-controls">
                    <label>Filter by Status: </label>
                    <select
                        className="default-select"
                        value={filterStatus}
                        onChange={e => {
                            setFilterStatus(e.target.value);
                            setRequestsPage(1);
                        }}
                    >
                        <option value="all">All Processed</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                    </select>
                </div>

                {currentRequests.length === 0 ? (
                    <div className="no-results">No requests to display.</div>
                ) : (
                    currentRequests.map(request => (
                        <div key={request.id} className={`request-item status-${request.status}`}>
                            <div className="request-type">
                                {request.request_type === 'pto' ? 'Paid Time Off' : 'Out of Office'} - {request.employee_name}
                            </div>
                            <div className="request-dates">
                                {formatDate(request.start_date)} - {formatDate(request.end_date)}
                            </div>
                            <div className="request-status">
                                {request.status === 'pending' ? 'Pending Approval' :
                                    request.status === 'approved' ? 'Approved' : 'Rejected'}
                            </div>
                            {request.notes && <div className="request-notes">{request.notes}</div>}
                            {request.updated_at && (
                                <div className="request-updated">
                                    Processed on: {new Date(request.updated_at).toLocaleString()}
                                </div>
                            )}
                        </div>
                    ))
                )}

                {totalPages > 1 && (
                    <div className="pagination">
                        {requestsPage > 1 && (
                            <div className='action alt' onClick={() => paginate(requestsPage - 1)}>
                                &laquo; Prev
                            </div>
                        )}

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                            <div
                                key={number}
                                className={`form-box alt small-text ${number === requestsPage ? 'active' : ''}`}
                                onClick={() => paginate(number)}
                            >
                                {number}
                            </div>
                        ))}

                        {requestsPage < totalPages && (
                            <div className='action alt' onClick={() => paginate(requestsPage + 1)}>
                                Next &raquo;
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderRequests = () => {
        return (
            <div className="requests-container">
                {!isAdmin() && (
                    <>
                        <button className='action alt' onClick={() => setNewRequest(true)}>
                            Create New Request
                        </button>
                        {newRequest && renderRequestForm()}
                    </>
                )}
                {!isAdmin() && renderUserRequests()}
                {isAdmin() && renderAdminApproval()}
            </div>
        );
    };

    // Function to check if a date is today
    const isToday = (date) => {
        const today = new Date();
        return date.getDate() === today.getDate() && 
               date.getMonth() === today.getMonth() && 
               date.getFullYear() === today.getFullYear();
    };

    return (
        <>
            <div className='page-container'>
                {screenWidth >= 768 ? (
                    <ul id='time-nav'>
                        <li className={`time-nav-item ${currentScreen === 0 ? 'active' : ''}`} onClick={() => setCurrentScreen(0)}>Time</li>
                        {/*<li className={`time-nav-item ${currentScreen === 1 ? 'active' : ''}`} onClick={() => setCurrentScreen(1)}>Overview</li>*/}
                        <li className={`time-nav-item ${currentScreen === 2 ? 'active' : ''}`} onClick={() => setCurrentScreen(2)}>Requests</li>
                        <li className={`time-nav-item ${currentScreen === 3 ? 'active' : ''}`} onClick={() => setCurrentScreen(3)}>Calendar</li>
                    </ul>
                ) : (
                    <select className='default-select' value={currentScreen} onChange={(e) => setCurrentScreen(Number(e.target.value))}>
                        <option value={0}>Time</option>
                        {/*<option value={1}>Overview</option>*/}
                        <option value={2}>Requests</option>
                        <option value={3}>Calendar</option>
                    </select>
                )}
                
                {currentScreen === 0 && renderTimekeeper()}
                {currentScreen === 2 && renderRequests()}
                {currentScreen === 3 && renderCalendar()}
                {showEventForm && renderEventForm()}
                
                {approvalFeedback.show && (
                    <Alert message={approvalFeedback.message} type={approvalFeedback.type} onClose={() => setApprovalFeedback({ show: false })} />
                )}
            </div>
        </>
    );
};